import { List } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import React from 'react';
import EnumTranslate from '@components/HelperComponents/EnumTranslate';
import { TerminalStatusBriefDTO, TerminalStatusDTO } from '@/model';
import {
  formatFullDateTime,
  getCommunicationStatus,
  getLastLoginTooltip,
  getPlannedNextConnectionTooltip,
} from '@/page/stopTerminalStatus/util/stopTerminalStatus.util';
import { FormField, FormFieldType, TranslationFunction } from '@/type';

const TruncatedCell = ({
  t,
  children,
}: {
  t: TranslationFunction;
  children: React.ReactNode;
}) => (
  <Tooltip title={t('general.table.cell.tooltip')}>
    <span className='table-cell-trucate'>{children}</span>
  </Tooltip>
);

export type TerminalStatusRow = TerminalStatusBriefDTO & { id: number };

export const terminalStatusTableColumns = (
  t: TranslationFunction,
  onOpenStatusHistory: (row: TerminalStatusRow) => void,
): GridColDef<TerminalStatusRow>[] => [
  {
    field: 'actions',
    headerName: t('general.table.header.actions'),
    sortable: false,
    filterable: false,
    flex: 1,
    renderCell: ({ row }) => (
      <IconButton onClick={() => onOpenStatusHistory(row)}>
        <Tooltip
          title={t('stop-terminal-status.terminal-table.header.view-status-history')}
        >
          <List />
        </Tooltip>
      </IconButton>
    ),
  },
  {
    field: 'productSnName',
    headerName: t('stop-terminal-status.terminal-table.header.product-sn-name'),
    align: 'left',
    flex: 2,
    renderCell: ({ row }) => <TruncatedCell t={t}>{row.productSnName}</TruncatedCell>,
  },
  {
    field: 'prodSerialNumber',
    headerName: t('stop-terminal-status.terminal-table.header.prod-serial-number'),
    align: 'left',
    flex: 2,
    renderCell: ({ row }) => <TruncatedCell t={t}>{row.prodSerialNumber}</TruncatedCell>,
  },
  {
    field: 'hwId',
    headerName: t('stop-terminal-status.terminal-table.header.hw-id'),
    align: 'left',
    flex: 2,
    renderCell: ({ row }) => <TruncatedCell t={t}>{row.hwId}</TruncatedCell>,
  },
  {
    field: 'state',
    headerName: t('stop-terminal-status.terminal-table.header.state'),
    flex: 1,
    renderCell: ({ row }) => <EnumTranslate data={row.state} />,
  },
  {
    field: 'insertDate',
    headerName: t('stop-terminal-status.terminal-table.header.insert-date'),
    flex: 2,
    valueGetter: (_value, row) => formatFullDateTime(row.insertDate),
    renderCell: ({ row }) => (
      <Tooltip title={getLastLoginTooltip(row.insertDate)}>
        <span>{formatFullDateTime(row.insertDate)}</span>
      </Tooltip>
    ),
  },
  {
    field: 'plannedNextConnection',
    headerName: t('stop-terminal-status.terminal-table.header.planned-next-connection'),
    flex: 2,
    valueGetter: (_value, row) => formatFullDateTime(row.plannedNextConnection),
    renderCell: ({ row }) => (
      <Tooltip title={getPlannedNextConnectionTooltip(row.plannedNextConnection, t)}>
        <span
          style={{ color: getCommunicationStatus(row.plannedNextConnection, t).color }}
        >
          {formatFullDateTime(row.plannedNextConnection)}
        </span>
      </Tooltip>
    ),
  },
  {
    field: 'versionHw',
    headerName: t('stop-terminal-status.terminal-table.header.version-hw'),
    flex: 1,
  },
  {
    field: 'versionFw',
    headerName: t('stop-terminal-status.terminal-table.header.version-fw'),
    flex: 1,
  },
  {
    field: 'simIccid',
    headerName: t('stop-terminal-status.terminal-table.header.sim-iccid'),
    flex: 1,
  },
  {
    field: 'last24HourTransferSpeed',
    headerName: t(
      'stop-terminal-status.terminal-table.header.last-24-hour-transfer-speed',
    ),
    flex: 1,
  },
];

export type TerminalStatusHistoryRow = TerminalStatusDTO & { id: number };

export const terminalStatusHistoryTableColumns = (
  t: TranslationFunction,
): GridColDef<TerminalStatusHistoryRow>[] => [
  {
    field: 'insertDate',
    headerName: t('stop-terminal-status.terminal-table.header.insert-date'),
    flex: 2,
    valueGetter: (_value, row) => formatFullDateTime(row.insertDate),
  },
  {
    field: 'state',
    headerName: t('stop-terminal-status.terminal-table.header.state'),
    flex: 1,
    renderCell: ({ row }) => <EnumTranslate data={row.state} />,
  },
  {
    field: 'signalStrength',
    headerName: t('stop-map.info.modal.signalStrength'),
    flex: 1,
  },
  {
    field: 'cpuTemp',
    headerName: t('stop-map.info.modal.cpuTemp'),
    flex: 1,
  },
  {
    field: 'ramUsed',
    headerName: t('stop-map.info.modal.ramUsed'),
    flex: 1,
  },
  {
    field: 'batteryLevel',
    headerName: t('stop-map.info.modal.batteryLevel'),
    flex: 1,
  },
  {
    field: 'batteryHealth',
    headerName: t('stop-map.info.modal.batteryHealth'),
    flex: 1,
  },
  {
    field: 'noPowerEstTime',
    headerName: t('stop-map.info.modal.noPowerEstTime'),
    type: 'number',
    flex: 1,
  },
  {
    field: 'fullChargeEstTime',
    headerName: t('stop-map.info.modal.fullChargeEstTime'),
    type: 'number',
    flex: 1,
  },
  {
    field: 'chargePower',
    headerName: t('stop-map.info.modal.chargePower'),
    flex: 1,
  },
  {
    field: 'averageCurrent',
    headerName: t('stop-map.info.modal.averageCurrent'),
    flex: 1,
  },
  {
    field: 'tamper',
    headerName: t('stop-map.info.modal.tamper'),
    flex: 1,
    renderCell: ({ row }) => (
      <b style={{ color: row.tamper ? 'red' : 'green' }}>
        {typeof row.tamper === 'undefined'
          ? ''
          : t(row.tamper ? 'general.yes' : 'general.no')}
      </b>
    ),
  },
  {
    field: 'versionHw',
    headerName: t('stop-terminal-status.terminal-table.header.version-hw'),
    flex: 1,
  },
  {
    field: 'versionFw',
    headerName: t('stop-terminal-status.terminal-table.header.version-fw'),
    flex: 1,
  },
  {
    field: 'simIccid',
    headerName: t('stop-terminal-status.terminal-table.header.sim-iccid'),
    flex: 1,
  },
];

export const terminalStatusHistoryFilterFormFields = (
  t: TranslationFunction,
): FormField[] => [
  {
    type: FormFieldType.DateTime,
    label: t(
      'stop-terminal-status.terminal-status-history-modal.filter.insert-date-greater-then',
    ),
    name: 'insertDateGreaterThen',
    xs: 4,
  },
  {
    type: FormFieldType.DateTime,
    label: t(
      'stop-terminal-status.terminal-status-history-modal.filter.insert-date-less-then',
    ),
    name: 'insertDateLessThen',
    xs: 4,
  },
];
